import React from "react";
import DOMPurify from "dompurify";
import { X, Rss, Clock, RefreshCw, FileText } from "lucide-react";

const fmtDate = (d) => d ? new Date(d).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "—";

const markdownToHtml = (md) => {
  if (!md) return "";
  const lines = md.split("\n");
  const out = [];
  let inList = false;
  const inline = (t) => t
    .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" style="color:#006874;text-decoration:underline;">$1</a>')
    .replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>")
    .replace(/\*(.+?)\*/g, "<em>$1</em>");
  for (const line of lines) {
    if (line.startsWith("### ")) {
      if (inList) { out.push("</ul>"); inList = false; }
      out.push(`<h3 style="font-family:Georgia,serif;font-size:15px;font-weight:700;color:#006874;margin:20px 0 6px;">${line.slice(4).trim()}</h3>`);
    } else if (line.startsWith("## ")) {
      if (inList) { out.push("</ul>"); inList = false; }
      out.push(`<h2 style="font-family:Georgia,serif;font-size:18px;font-weight:700;color:#191c1d;margin:28px 0 10px;padding-bottom:6px;border-bottom:2px solid #006874;">${line.slice(3).trim()}</h2>`);
    } else if (line.startsWith("# ")) {
      if (inList) { out.push("</ul>"); inList = false; }
      out.push(`<h1 style="font-family:Georgia,serif;font-size:22px;font-weight:700;color:#191c1d;margin:0 0 14px;">${line.slice(2).trim()}</h1>`);
    } else if (/^(---|___|\*\*\*)$/.test(line.trim())) {
      if (inList) { out.push("</ul>"); inList = false; }
      out.push('<hr style="border:none;border-top:1px solid #e7e8e9;margin:20px 0;">');
    } else if (/^[-*+] /.test(line)) {
      if (!inList) { out.push('<ul style="padding-left:20px;margin:10px 0;">'); inList = true; }
      out.push(`<li style="color:#3b494c;font-size:14px;line-height:1.7;margin-bottom:5px;">${inline(line.slice(2).trim())}</li>`);
    } else if (line.trim() === "") {
      if (inList) { out.push("</ul>"); inList = false; }
      out.push("");
    } else {
      if (inList) { out.push("</ul>"); inList = false; }
      out.push(`<p style="color:#3b494c;font-size:14px;line-height:1.7;margin:0 0 14px;">${inline(line)}</p>`);
    }
  }
  if (inList) out.push("</ul>");
  return out.join("\n");
};

export default function BeehiivPreviewModal({
  dm, beehiivPreview, setBeehiivPreview,
  publishingBeehiiv, publishToBeehiiv,
}) {
  if (!beehiivPreview) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: "rgba(25,28,29,0.72)", backdropFilter: "blur(6px)" }}
      onClick={(e) => { if (e.target === e.currentTarget) setBeehiivPreview(null); }}
    >
      <div
        data-testid="beehiiv-preview-modal"
        className={`relative flex flex-col w-full max-w-3xl rounded-2xl overflow-hidden shadow-2xl ${dm ? "bg-[#111618]" : "bg-white"}`}
        style={{ maxHeight: "90vh" }}
      >
        <div className={`flex items-start justify-between px-8 py-5 border-b ${dm ? "border-white/6 bg-[#0c1011]" : "border-black/6 bg-[#f8f9fa]"}`}>
          <div>
            <div className="flex items-center gap-2.5 mb-1">
              <div className={`w-7 h-7 rounded-lg flex items-center justify-center ${dm ? "bg-amber-400/10" : "bg-amber-50"}`}>
                <Rss className={`w-3.5 h-3.5 ${dm ? "text-amber-400" : "text-amber-600"}`} />
              </div>
              <span className={`text-xs font-bold uppercase tracking-widest ${dm ? "text-amber-400" : "text-amber-600"}`}>Beehiiv Preview</span>
            </div>
            <h2 className={`editorial-heading text-lg font-semibold leading-snug ${dm ? "text-slate-100" : "text-[#191c1d]"}`}>{beehiivPreview.title}</h2>
            <p className={`category-tag mt-0.5 ${dm ? "text-slate-500" : "text-[#6b7a7d]"}`}>
              {beehiivPreview.word_count} words · {beehiivPreview.article_count} articles · Created {fmtDate(beehiivPreview.created_at)}
            </p>
          </div>
          <button onClick={() => setBeehiivPreview(null)} data-testid="beehiiv-preview-close"
            className={`flex-shrink-0 p-1.5 rounded-lg mt-0.5 transition-colors ${dm ? "text-slate-500 hover:bg-white/8 hover:text-slate-300" : "text-[#6b7a7d] hover:bg-black/5"}`}>
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="overflow-y-auto flex-1 px-8 py-6" style={{ background: "#ffffff" }}>
          <div style={{ borderBottom: "1px solid #e7e8e9", paddingBottom: 16, marginBottom: 24 }}>
            <p style={{ fontFamily: "Georgia,serif", fontSize: 22, fontWeight: 700, color: "#191c1d", margin: "0 0 6px" }}>{beehiivPreview.title}</p>
            <p style={{ fontFamily: "sans-serif", fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.1em", color: "#6b7a7d" }}>
              TourismIntel · {fmtDate(beehiivPreview.created_at)}
            </p>
          </div>
          <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(markdownToHtml(beehiivPreview.content)) }} />
        </div>

        <div className={`flex items-center justify-between px-8 py-4 border-t ${dm ? "border-white/6 bg-[#0c1011]" : "border-black/6 bg-[#f8f9fa]"}`}>
          <div className="flex items-center gap-4 flex-wrap">
            {(() => {
              const words = beehiivPreview.word_count || 0;
              const mins = Math.max(1, Math.round(words / 200));
              return (
                <div className="flex items-center gap-3">
                  <span data-testid="beehiiv-word-count" className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ${dm ? "bg-[#1a2122] text-slate-400" : "bg-[#f3f4f5] text-[#6b7a7d]"}`}>
                    <FileText className="w-3 h-3" />{words.toLocaleString()} words
                  </span>
                  <span data-testid="beehiiv-read-time" className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ${dm ? "bg-amber-400/10 text-amber-400" : "bg-amber-50 text-amber-700"}`}>
                    <Clock className="w-3 h-3" />{mins} min read
                  </span>
                </div>
              );
            })()}
            <p className={`category-tag ${dm ? "text-slate-500" : "text-[#6b7a7d]"}`}>
              Will publish as <strong>confirmed</strong> on Beehiiv immediately.
            </p>
          </div>
          <div className="flex items-center gap-2.5 flex-shrink-0 ml-6">
            <button onClick={() => setBeehiivPreview(null)} data-testid="beehiiv-preview-cancel"
              className={`px-4 py-2 rounded-full category-tag transition-colors ${dm ? "bg-[#1a2122] text-slate-400 hover:bg-[#252e2f]" : "bg-[#f3f4f5] text-[#6b7a7d] hover:bg-[#e7e8e9]"}`}>
              Cancel
            </button>
            <button onClick={() => publishToBeehiiv(beehiivPreview)} disabled={!!publishingBeehiiv[beehiivPreview.id]}
              data-testid="beehiiv-preview-confirm"
              className={`px-5 py-2 rounded-full category-tag font-semibold transition-all flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed ${dm ? "bg-amber-400/20 text-amber-300 hover:bg-amber-400/30" : "bg-amber-600 text-white hover:bg-amber-700"}`}>
              {publishingBeehiiv[beehiivPreview.id]
                ? <><RefreshCw className="w-3.5 h-3.5 animate-spin" />Publishing…</>
                : <><Rss className="w-3.5 h-3.5" />Confirm & Publish</>}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
